import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FolderOpen, Plus, Download, Eye, Clock, CheckCircle, AlertCircle, XCircle, 
  LogOut, User, TrendingUp, Award, MessageCircle, Calendar, Send, X, Shield, ArrowLeft,
  Lock, Settings, ChevronDown, AlertTriangle, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { projectsApi, expertApi, clientApi } from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';
import ProjectCommunication from '../components/ProjectCommunication';
import { useUnreadMessages, UnreadDot } from '../lib/useUnreadMessages';
import ExpertVerdictGauge from '../components/ExpertVerdictGauge';
import ProjectMiniMap from '../components/ProjectMiniMap';
import { usePartner } from '../lib/partnerContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'En cours', icon: Clock, color: 'bg-blue-100 text-blue-700', progress: 25 },
  in_review: { label: 'En analyse', icon: AlertCircle, color: 'bg-amber-100 text-amber-700', progress: 60 },
  completed: { label: 'Fait ✓', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', progress: 100 },
  rejected: { label: 'Refusé', icon: XCircle, color: 'bg-red-100 text-red-700', progress: 100 }
};

const SCORE_CONFIG = {
  green: { label: 'Validé', color: 'bg-emerald-500 text-white' },
  orange: { label: 'Risqué', color: 'bg-amber-500 text-white' },
  red: { label: 'Refusé', color: 'bg-red-500 text-white' }
};

// Expert Badge Component - variant: "light" (default) or "dark" (for navy background)
const ExpertBadge = ({ className = "", variant = "light" }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
    variant === "dark" 
      ? "bg-white/10 border border-white/20" 
      : "bg-navy/5 border border-navy/20"
  } ${className}`}>
    <Award className="w-3.5 h-3.5 text-gold" />
    <span className={`text-xs font-medium ${variant === "dark" ? "text-gold" : "text-navy"}`}>
      25 ans d'expérience bâtiment
    </span>
  </div>
);

// Expert Analysis Message - Shown when project is in review
const ExpertAnalysisMessage = () => (
  <div className="bg-gradient-to-r from-navy/5 to-gold/5 border border-gold/30 rounded-xl p-4 mt-4">
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-navy flex items-center justify-center">
        <Award className="w-5 h-5 text-gold" />
      </div>
      <div>
        <p className="text-sm font-semibold text-navy mb-1">Analyse en cours par notre expert</p>
        <p className="text-xs text-navy/70 italic leading-relaxed">
          "Votre dossier est examiné avec l'expertise de 25 ans de terrain, entre chantiers complexes et arbitrages immobiliers. Chaque détail compte pour sécuriser votre investissement."
        </p>
        <p className="text-xs text-gold font-semibold mt-2">— L'Expert</p>
      </div>
    </div>
  </div>
);

// Chat Component for Premium users - REAL API CONNECTED
const PremiumChat = ({ isOpen, onClose, token }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = React.useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, 
        { content: newMessage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNewMessage('');
      fetchMessages();
      toast.success('Message envoyé !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      fetchMessages();
      // Refresh every 30 seconds when open
      const interval = setInterval(fetchMessages, 30000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white rounded-2xl shadow-2xl border-2 border-navy/10 z-50 overflow-hidden">
      <div className="bg-navy text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-black" />
          </div>
          <div>
            <h4 className="font-semibold">Chat Expert</h4>
            <p className="text-xs text-white/70">Réponse rapide (Master VIP)</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10">
          <X className="w-5 h-5" />
        </Button>
      </div>
      
      <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-navy border-t-transparent rounded-full"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Aucun message</p>
            <p className="text-xs mt-1">Envoyez votre première question à l'Expert !</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] p-3 rounded-2xl ${
                msg.sender_type === 'client' 
                  ? 'bg-navy text-white rounded-br-sm' 
                  : 'bg-white border shadow-sm rounded-bl-sm'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                <p className={`text-xs mt-1 ${msg.sender_type === 'client' ? 'text-white/60' : 'text-gray-400'}`}>
                  {formatTime(msg.created_at)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="p-3 border-t bg-white">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Votre message..."
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            className="flex-1"
          />
          <Button onClick={sendMessage} disabled={sending || !newMessage.trim()} className="bg-gold hover:bg-gold/90 text-black">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, logout, isExpert, refreshUser } = useAuth();
  const partner = usePartner(searchParams.toString(), user);
  const [projects, setProjects] = useState([]);
  const { unread: unreadByProject, refresh: refreshUnread } = useUnreadMessages(projects, token);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, completed: 0, totalMargin: 0 });
  const [chatOpen, setChatOpen] = useState(searchParams.get('chat') === 'open');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Appointment states
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [myAppointments, setMyAppointments] = useState([]);
  const [bookingAppointment, setBookingAppointment] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Project communication modal (messaging + documents per dossier)
  const [commProject, setCommProject] = useState(null);  // selected project for the modal

  const isPremium = user?.subscription_type === 'master';
  const userName = user?.name || user?.email?.split('@')[0] || 'Client';
  const userPackage = user?.subscription_type || user?.selected_package;
  const userCredits = user?.credits ?? 0;
  const isLowCredits = userCredits <= 1 && userCredits >= 0;
  const noCredits = userCredits === 0;
  
  const PACKAGE_INFO = {
    flash: { name: 'Flash', color: 'bg-gray-100 text-gray-800', credits: 1 },
    pro: { name: 'Marchand Pro', color: 'bg-gold/20 text-gold', credits: 4 },
    master: { name: 'Master VIP', color: 'bg-navy text-white', credits: 10 }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current || !passwordData.new) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (passwordData.new.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/client/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success('Mot de passe modifié avec succès !');
      setShowPasswordModal(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    if (user?.subscription_type === 'master') {
      fetchMyAppointments();
    }
  }, []);
  
  // Appointment functions
  const fetchMyAppointments = async () => {
    try {
      const res = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyAppointments(res.data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };
  
  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/appointments/available-slots?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableSlots(res.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };
  
  const bookAppointment = async () => {
    if (!selectedDate || !selectedSlot) return;
    setBookingAppointment(true);
    try {
      await axios.post(`${API}/appointments`, {
        date: selectedDate,
        time_slot: selectedSlot
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RDV confirmé ! Email de confirmation envoyé. (vérifiez vos spams)');
      setShowAppointmentModal(false);
      setSelectedDate('');
      setSelectedSlot('');
      fetchMyAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setBookingAppointment(false);
    }
  };
  
  const cancelAppointment = async (appointmentId) => {
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('RDV annulé');
      fetchMyAppointments();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };
  
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedSlot('');
    if (date) {
      fetchAvailableSlots(date);
    }
  };
  
  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const fetchProjects = async () => {
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data);
      
      // Calculate stats
      const completed = response.data.filter(p => p.status === 'completed');
      const totalMargin = completed.reduce((sum, p) => sum + (p.net_margin || 0), 0);
      setStats({
        total: response.data.length,
        completed: completed.length,
        totalMargin
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Erreur lors du chargement des dossiers');
    } finally {
      setLoading(false);
    }
  };

  const becomeExpert = async () => {
    try {
      const response = await axios.post(`${API}/auth/become-expert`);
      // Update token in localStorage
      localStorage.setItem('token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      toast.success('Vous êtes maintenant Expert ! Rechargez la page.');
      // Refresh to apply changes
      window.location.reload();
    } catch (error) {
      console.error('Error becoming expert:', error);
      toast.error('Erreur lors de la promotion');
    }
  };

  const downloadPdf = (projectId) => {
    const url = clientApi.getPdfUrl(projectId, token);
    window.open(url, '_blank');
  };

  const formatPrice = (price) => {
    if (!price) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-navy text-white shadow-lg" style={partner.color ? { backgroundColor: partner.color } : undefined} data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-to-home-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate(partner.home)}
                className="text-white/70 hover:text-white hover:bg-white/10 -ml-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Retour
              </Button>
              <div className="h-6 w-px bg-white/20" />
              {partner.isPartner && partner.logo && (
                <img src={partner.logo} alt={partner.name || 'Partenaire'} className="h-10 w-10 rounded-lg object-cover bg-white/10" data-testid="dashboard-partner-logo" />
              )}
              <div>
                <h1 className="font-heading text-xl font-bold tracking-tight cursor-pointer" onClick={() => navigate(partner.home)} data-testid="dashboard-brand-name">
                  {(partner.isPartner && partner.name ? partner.name : "L'ŒIL DU MARCHAND").toUpperCase()}
                </h1>
                <p className="text-xs text-white/60">Audit immobilier • Expertise Bâtiment & Travaux</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Bonjour message */}
              <span className="text-white/80 text-sm hidden sm:block">
                Bonjour <strong className="text-gold">{userName}</strong>
              </span>
              
              {!partner.isPartner && <ExpertBadge variant="dark" className="hidden lg:flex" />}
              {isExpert ? (
                <Button
                  data-testid="expert-dashboard-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/expert')}
                  className="border-gold text-gold hover:bg-gold hover:text-black font-semibold"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Dashboard Expert
                </Button>
              ) : partner.isPartner ? null : (
                <Button
                  data-testid="become-expert-btn"
                  variant="outline"
                  size="sm"
                  onClick={becomeExpert}
                  className="border-gold/50 text-gold hover:bg-gold hover:text-black"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Devenir Expert
                </Button>
              )}
              
              {/* User Menu Dropdown */}
              <div className="relative">
                <Button
                  data-testid="user-menu-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="text-white hover:bg-white/10"
                >
                  <User className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Mon Compte</span>
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
                
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-50">
                    <button
                      onClick={() => { navigate('/account'); setShowUserMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" /> Paramètres
                    </button>
                    <button
                      onClick={() => { setShowPasswordModal(true); setShowUserMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      data-testid="change-password-menu-btn"
                    >
                      <Lock className="w-4 h-4" /> Changer mot de passe
                    </button>
                    <hr className="my-1" />
                    <button
                      onClick={() => { logout(); setShowUserMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" /> Déconnexion
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Premium Banner for Master subscribers */}
      {isPremium && (
        <div className="bg-gradient-to-r from-gold/20 to-amber-50 border-b border-gold/30">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className="bg-gold text-black">Master VIP</Badge>
                <span className="text-sm text-navy">Accès prioritaire • Chat direct • Visio mensuelle</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="open-chat-btn"
                  size="sm"
                  onClick={() => setChatOpen(true)}
                  className="bg-navy hover:bg-navy/90 text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat Expert
                </Button>
                <Button
                  data-testid="book-visio-btn"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAppointmentModal(true)}
                  className="border-navy text-navy hover:bg-navy hover:text-white"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Réserver RDV
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Bandeau Partenaire — visible pour les clients liés à un partenaire */}
        {user?.partner_subdomain && partner.isPartner && (
          <div
            className="mb-6 rounded-xl p-4 flex items-center gap-4 shadow-sm"
            style={{ backgroundColor: partner.color ? `${partner.color}18` : '#f0fdf4', border: `1.5px solid ${partner.color || '#10b981'}44` }}
            data-testid="partner-client-banner"
          >
            {partner.logo && (
              <img
                src={partner.logo}
                alt={partner.name || 'Partenaire'}
                className="h-12 w-12 rounded-lg object-cover flex-shrink-0"
                style={{ border: `2px solid ${partner.color || '#10b981'}55` }}
              />
            )}
            <div className="min-w-0">
              <p className="font-semibold text-sm" style={{ color: partner.color || '#065f46' }}>
                Votre dossier est suivi par
              </p>
              <p className="font-bold text-base text-navy truncate">
                {(partner.name || partner.subdomain || 'votre partenaire').toUpperCase()}
              </p>
            </div>
          </div>
        )}
        {/* Low/No Credits Alert — masqué pour les clients partenaires */}
        {noCredits && !user?.partner_subdomain && (
          <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-full shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="font-bold text-red-800">Plus de crédits disponibles !</p>
                <p className="text-sm text-red-600">Rechargez pour créer de nouveaux audits.</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate(partner.link('/?tab=audit'))}
              className="bg-red-600 hover:bg-red-700 text-white font-bold w-full sm:w-auto shrink-0"
            >
              🔋 Recharger maintenant
            </Button>
          </div>
        )}
        
        {isLowCredits && !noCredits && !user?.partner_subdomain && (
          <div className="mb-6 bg-orange-50 border-2 border-orange-300 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="font-bold text-orange-800">Plus qu'un crédit restant !</p>
                <p className="text-sm text-orange-600">Pensez à recharger pour ne pas être bloqué.</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate(partner.link('/?tab=audit'))}
              variant="outline"
              className="border-orange-500 text-orange-700 hover:bg-orange-100 font-bold"
            >
              🔋 Recharger
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-navy/10">
                  <FolderOpen className="w-6 h-6 text-navy" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Dossiers ouverts</p>
                  <p className="text-2xl font-bold text-navy">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-emerald-100">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Dossiers terminés</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-gold/10">
                  <TrendingUp className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Marge totale détectée</p>
                  <p className="text-2xl font-bold text-gold">{formatPrice(stats.totalMargin)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Subscription Card — masquée pour les clients partenaires */}
          {!user?.partner_subdomain && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              {(() => {
                const audits_used = projects.length;
                const audits_remaining = userCredits;
                const pkgInfo = userPackage && PACKAGE_INFO[userPackage] ? PACKAGE_INFO[userPackage] : null;
                const audits_total = pkgInfo ? pkgInfo.credits : (audits_used + audits_remaining);
                const has_pack = !!userPackage && audits_total > 0;
                const pct = audits_total > 0 ? Math.min(100, (audits_used / audits_total) * 100) : 0;

                let statusText, statusColor;
                if (!has_pack) {
                  statusText = 'Aucun audit acheté';
                  statusColor = 'text-gray-500';
                } else if (audits_remaining > 0) {
                  statusText = `✅ ${audits_remaining} audit${audits_remaining > 1 ? 's' : ''} disponible${audits_remaining > 1 ? 's' : ''}`;
                  statusColor = 'text-emerald-600';
                } else if (audits_used > 0 && stats.completed < audits_used) {
                  statusText = '⏳ Audit en cours d\'analyse';
                  statusColor = 'text-orange-600';
                } else {
                  statusText = '✅ Pack terminé — rechargez pour un nouvel audit';
                  statusColor = 'text-blue-600';
                }

                return (
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-purple-100">
                          <Award className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Mon Abonnement</p>
                          {pkgInfo ? (
                            <div className="flex items-center gap-2">
                              <Badge className={pkgInfo.color}>{pkgInfo.name}</Badge>
                              <span className="text-xs text-muted-foreground">· {audits_total} audit{audits_total > 1 ? 's' : ''} acheté{audits_total > 1 ? 's' : ''}</span>
                            </div>
                          ) : (
                            <p className="text-sm font-medium text-navy">Aucun</p>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={audits_remaining === 0 ? 'default' : 'outline'}
                        onClick={() => navigate('/account')}
                        className={audits_remaining === 0 ? 'bg-gold hover:bg-gold/90 text-black text-xs' : 'text-xs'}
                        data-testid="manage-subscription-btn"
                      >
                        {audits_remaining === 0 && has_pack ? 'Recharger' : pkgInfo ? 'Gérer' : 'S\'abonner'}
                      </Button>
                    </div>

                    {has_pack && (
                      <>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all ${audits_remaining === 0 ? 'bg-orange-400' : 'bg-emerald-500'}`}
                            style={{ width: `${pct}%` }}
                            data-testid="audits-progress-bar"
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{audits_used}/{audits_total} utilisés</span>
                          <span className={`font-semibold ${statusColor}`} data-testid="audits-status-text">{statusText}</span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
          )}
        </div>

        {/* Projects Section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-navy flex items-center gap-2">
            <FolderOpen className="w-6 h-6" />
            Mes Dossiers
          </h2>
          <Button
            data-testid="new-project-btn"
            onClick={() => navigate(partner.link('/'))}
            className="bg-gold hover:bg-gold/90 text-black font-semibold shadow-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Dossier
          </Button>
        </div>

        {projects.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <FolderOpen className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-navy mb-2">Aucun dossier</h3>
              <p className="text-muted-foreground mb-4">Commencez par analyser votre premier bien immobilier</p>
              <Button
                data-testid="create-first-project-btn"
                onClick={() => navigate(partner.link('/'))}
                className="bg-gold hover:bg-gold/90 text-black font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer mon premier dossier
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => {
              const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.pending;
              const StatusIcon = status.icon;
              const score = project.expert_score ? SCORE_CONFIG[project.expert_score] : null;
              
              return (
                <Card key={project.id} data-testid={`project-card-${project.id}`} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                      {/* Score Badge */}
                      <div className="flex-shrink-0">
                        {score ? (
                          <div className={`w-16 h-16 rounded-xl ${score.color} flex items-center justify-center`}>
                            <CheckCircle className="w-8 h-8" />
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center">
                            <Clock className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-heading font-semibold text-navy text-lg truncate">
                              {project.address}
                            </h3>
                            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                              <span>{project.surface} m²</span>
                              <span>•</span>
                              <span>{formatPrice(project.purchase_price)}</span>
                              <span>•</span>
                              <span>{formatDate(project.created_at)}</span>
                            </div>
                          </div>
                          <Badge className={status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        
                        {/* Progress */}
                        {project.status !== 'completed' && project.status !== 'rejected' && (
                          <div className="mt-4">
                            <Progress value={status.progress} className="h-1.5" />
                            {project.status === 'in_review' ? (
                              <ExpertAnalysisMessage />
                            ) : (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <Award className="w-3 h-3 text-gold" />
                                En attente d'analyse par l'expert...
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Financial Summary */}
                        {project.status === 'completed' && project.net_margin !== null && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">Coût Total</p>
                                <p className="font-semibold text-navy">
                                  {formatPrice(project.purchase_price + (project.purchase_price * project.notary_rate) + project.total_works)}
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Prix Revente</p>
                                <p className="font-semibold text-navy">{formatPrice(project.estimated_resale_price)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Marge Nette</p>
                                <p className={`font-bold ${project.net_margin > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {formatPrice(project.net_margin)}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Œil de l'Expert : verdict système avec jauge */}
                        <div className="mt-4">
                          <ExpertVerdictGauge projectId={project.id} />
                        </div>

                        {/* Mini-carte de localisation */}
                        <div className="mt-4">
                          <ProjectMiniMap
                            latitude={project.latitude}
                            longitude={project.longitude}
                            address={project.address}
                            address_precision={project.address_precision}
                            height={200}
                          />
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-2 mt-4">
                          <Button
                            data-testid={`open-comm-${project.id}`}
                            onClick={() => setCommProject(project)}
                            variant="outline"
                            className="border-navy text-navy hover:bg-navy hover:text-white relative"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Messagerie & Documents
                            <UnreadDot count={unreadByProject[project.id]} className="absolute -top-2 -right-2" />
                          </Button>
                          {project.status === 'completed' && (
                            <Button
                              data-testid={`download-pdf-${project.id}`}
                              onClick={() => downloadPdf(project.id)}
                              className="bg-gold hover:bg-gold/90 text-black"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger le Rapport
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Upsell Banner for non-premium users */}
        {!isPremium && stats.completed >= 1 && (
          <Card className="mt-8 border-0 shadow-lg bg-gradient-to-r from-navy to-navy-700 text-white overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <div className="p-4 rounded-2xl bg-gold/20">
                  <Award className="w-10 h-10 text-gold" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-xl font-bold">Passez au niveau supérieur</h3>
                  <p className="text-white/70 mt-1">
                    Avec <span className="text-gold font-semibold">Master VIP</span>, bénéficiez d'audits illimités, 
                    d'un chat direct avec l'expert et d'une session visio mensuelle.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/account')}
                  className="bg-gold hover:bg-gold/90 text-black font-semibold px-6"
                >
                  Découvrir les offres
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Premium Chat */}
      {isPremium && <PremiumChat isOpen={chatOpen} onClose={() => setChatOpen(false)} token={token} />}

      {/* Project Communication Modal — messagerie + documents par dossier */}
      <Dialog open={!!commProject} onOpenChange={(o) => { if (!o) { setCommProject(null); refreshUnread(); } }}>
        <DialogContent className="max-w-3xl bg-navy-800 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-gold" />
              Dossier · {commProject?.address}
              <span className="text-xs font-normal text-white/50 ml-2">#{commProject?.id?.slice(0, 8).toUpperCase()}</span>
            </DialogTitle>
          </DialogHeader>
          {commProject && (
            <ProjectCommunication projectId={commProject.id} token={token} />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Chat FAB for premium users */}
      {isPremium && !chatOpen && (
        <button
          data-testid="chat-fab"
          onClick={() => setChatOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gold rounded-full shadow-lg flex items-center justify-center hover:bg-gold/90 transition-colors z-40"
        >
          <MessageCircle className="w-6 h-6 text-black" />
        </button>
      )}

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-gold" />
              Changer mon mot de passe
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mot de passe actuel</Label>
              <Input
                type="password"
                value={passwordData.current}
                onChange={(e) => setPasswordData({...passwordData, current: e.target.value})}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label>Nouveau mot de passe (min. 6 caractères)</Label>
              <Input
                type="password"
                value={passwordData.new}
                onChange={(e) => setPasswordData({...passwordData, new: e.target.value})}
                placeholder="••••••••"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})}
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="w-full bg-gold hover:bg-gold/90 text-black"
              data-testid="change-password-submit-btn"
            >
              {changingPassword ? 'Modification...' : 'Modifier le mot de passe'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Appointment Booking Modal */}
      <Dialog open={showAppointmentModal} onOpenChange={setShowAppointmentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gold" />
              Prendre rendez-vous avec l'Expert
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* My upcoming appointments */}
            {myAppointments.filter(a => new Date(a.date) >= new Date() && a.status !== 'cancelled').length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <h4 className="font-semibold text-emerald-800 mb-2">Vos RDV à venir</h4>
                {myAppointments
                  .filter(a => new Date(a.date) >= new Date() && a.status !== 'cancelled')
                  .map((apt) => (
                    <div key={apt.id} className="flex items-center justify-between bg-white rounded-lg p-3 mt-2">
                      <div>
                        <p className="font-medium text-navy">
                          {new Date(apt.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </p>
                        <p className="text-gold font-semibold">{apt.time_slot}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 border-red-300 hover:bg-red-50"
                        onClick={() => cancelAppointment(apt.id)}
                      >
                        Annuler
                      </Button>
                    </div>
                  ))}
              </div>
            )}
            
            {/* Date picker */}
            <div>
              <Label className="text-navy font-semibold">Choisissez une date</Label>
              <Input
                type="date"
                min={getMinDate()}
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="mt-2"
              />
            </div>
            
            {/* Time slots */}
            {selectedDate && (
              <div>
                <Label className="text-navy font-semibold">Créneaux disponibles</Label>
                {loadingSlots ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin w-6 h-6 border-2 border-gold border-t-transparent rounded-full"></div>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground bg-gray-50 rounded-lg mt-2">
                    Aucun créneau disponible ce jour
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedSlot(slot)}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          selectedSlot === slot
                            ? 'border-gold bg-gold/10 text-navy'
                            : 'border-gray-200 hover:border-gold/50'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Confirm button */}
            <Button
              onClick={bookAppointment}
              disabled={!selectedDate || !selectedSlot || bookingAppointment}
              className="w-full bg-gold hover:bg-gold/90 text-black"
            >
              {bookingAppointment ? 'Réservation...' : 'Confirmer le RDV'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
